
import { Component, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import Loading from "../../../components/loading.vue";

import ExamV3Custom, {
  ExamV3Tem
} from "@/lib/model/exambuilder/exam-v3/exam-v3";
import { PsychologicalMotivation } from "@/lib/model/exambuilder/exam-v3/exam-v3-sub";
import { Check } from "@/lib/enum/check";
import ModelCard from "@/views/ai-builder-v3/components/modelCard/index.vue";
import { TemplateList, ListCommonRequest } from "@/lib/newExam/template";
import { PostList, Post, PostGroup } from "@/lib/newExam/post";
import { PsycGaugeConfig } from "@/lib/model/exambuilder/exam-v2";

const psycGaugeIdMap = {
  [Check.BIG_FIVE_240]: 1,
  [Check.DISC]: 3,
  [Check.BIG_FIVE_60]: 4,
  [Check.DVF]: 5
};

@Component({
  components: { Loading, ModelCard }
})
export default class BasicConfiguration extends Vue {
  @Prop({ type: ExamV3Custom, default: null }) exam!: ExamV3Custom; //考试Model
  @Prop({ type: String, default: "" }) fromTemplateStoreId!: string; //模板库带入id
  @Prop({ default: false }) setWeb!: boolean; //内部使用 web配置
  @Prop({ default: false }) isEdit!: boolean; //创建或编辑
  @Prop({ default: false }) isTemplateNextAfter!: boolean; // 是否进入过下一步
  @Prop() cacheTemplateList!: ExamV3Tem[];
  @Prop() cachePostList!: PostGroup[];
  @Prop({ default: () => [new PsycGaugeConfig()] })
  mindList!: PsycGaugeConfig[]; // 心理测评list

  formData = {
    name: "",
    remark: "",
    positionId: 0, //岗位
    templateId: 0 //模版
  };
  rules = {
    name: [{ required: true, message: "请输入面试名称", trigger: "blur" }],
    positionId: [
      {
        required: true,
        message: "请选择岗位",
        trigger: ["blur", "change"],
        validator: (rule: any, value: any, callback: any) => {
          if (!value) {
            callback(new Error("请选择岗位"));
          } else {
            callback();
          }
        }
      }
    ],
    templateId: [
      { required: true, message: "请选择或自定义模版", trigger: "change" }
    ]
  };
  dataReady = false;

  // 输入长度校验方法
  validateInputMaxLength(e: any, value: string, max: number) {
    if (value.length !== max || e.keyCode < 48 || e.keyCode > 90) return;

    this.$message.warning(`已达到${max}个字，无法继续输入！`);
  }

  options: PostGroup[] = [];
  loading = false;
  templateList: ExamV3Tem[] = [];

  @Emit()
  status() {
    return this.dataReady;
  }

  // 模版
  targetTemplate(tem: ExamV3Tem) {
    const newExam = new ExamV3Custom();
    newExam.basicInformation.name = this.formData.name;
    newExam.basicInformation.remark = this.formData.remark;
    newExam.basicInformation.positionId = +this.formData.positionId;
    newExam.basicInformation.positionName =
      this.options.find(item => item.positionId === +this.formData.positionId)
        ?.name || "";
    newExam.templateId = this.formData.templateId;
    newExam.assign(tem || new ExamV3Tem());
    this.$emit("selectTemp", newExam);
  }

  nextAfter(data: { nextAfter: boolean; examV3Tem: ExamV3Tem }) {
    this.$emit("templateNextAfter", data);
  }

  // 处理校验规则
  @Watch("isEdit", { immediate: true })
  handleRules() {
    this.rules.positionId[0].required = !this.isEdit;
  }

  visibleChange(flag: boolean) {
    if (!flag) {
      (this.$refs["form"] as any)?.validateField("positionId");
      this.$nextTick(() => {
        this.onDataChange();
        if (this.exam.templateId) {
          this.targetTemplate(this.exam);
        }
      });
    } else if (!this.formData.positionId) {
      // 没有选择时默认展开第一个岗位分类
      const cascader = this.$refs.cascader as any;
      const panel = cascader?.$refs.panel;
      if (!panel) return;
      const node = panel.getNodeByValue([this.options[0].positionId]);
      panel.activePath = [node];
      panel.expandNodes([node]);
    }
  }

  async created() {
    this.status();
    this.initData();

    if (!this.cachePostList.length) {
      await this.getPostList();
    } else {
      this.options = this.cachePostList;
    }
    if (!this.cacheTemplateList.length) {
      await this.getTemplateList();
    } else {
      this.templateList = this.cacheTemplateList;
    }

    this.dataReady = true;
    this.status();
  }

  // 心理相关题目处理
  initSelectedPsyc(psychologicalMotivation: PsychologicalMotivation) {
    const { psyc1, psyc4 } = psychologicalMotivation;

    if (psyc1.use === 1) {
      const psycId = psycGaugeIdMap[Check.BIG_FIVE_240];
      const mindObj =
        this.mindList.find(item => item.psycGaugeId === psycId) ||
        new PsycGaugeConfig();

      psyc1.psycGaugeSubDimensionList = mindObj.dimensionList.reduce(
        (pre: any[], item) => {
          const list = item.subDimensionList
            .filter(sItem =>
              psyc1.psycGaugeSubDimensionIdList.includes(
                sItem.psycGaugeDimensionId
              )
            )
            .map(sItem => ({
              id: sItem.psycGaugeDimensionId,
              questionCount: sItem.questionCount
            }));

          return [...pre, ...list];
        },
        []
      );
    }

    if (psyc4.use === 1) {
      const psycId = psycGaugeIdMap[Check.BIG_FIVE_60];
      const mindObj =
        this.mindList.find(item => item.psycGaugeId === psycId) ||
        new PsycGaugeConfig();

      psyc4.psycGaugeSubDimensionList = mindObj.dimensionList
        .filter(item =>
          psyc4.psycGaugeSubDimensionIdList.includes(item.psycGaugeDimensionId)
        )
        .map(item => ({
          id: item.psycGaugeDimensionId,
          questionCount: item.questionCount
        }));
    }
  }

  // 获取模版
  async getTemplateList() {
    const result = await TemplateList.get(new ListCommonRequest());
    this.templateList = result.list.filter(t => {
      this.initSelectedPsyc(t.processInformation.psychologicalMotivation);
      return t.canUsed === 1;
    });
    // 传递父级备份
    this.$emit("update:cacheTemplateList", this.templateList);
  }
  // 获取岗位列表
  async getPostList() {
    const companyId = this.$route.query.companyId as string;
    const result = await PostList.get(companyId);
    this.options = result.positionGroups;
    // 传递父级备份
    this.$emit("update:cachePostList", this.options);
  }

  remoteMethod(query: string) {
    if (query !== "") {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.options = this.options.filter(item => {
          return item.name.toLowerCase().indexOf(query.toLowerCase()) > -1;
        });
      }, 200);
    } else {
      this.options = [];
    }
  }

  @Watch("exam")
  initData() {
    this.formData.name = this.exam.basicInformation.name;
    this.formData.remark = this.exam.basicInformation.remark;
    // // 岗位
    this.formData.positionId =
      !this.isEdit && this.exam.basicInformation.positionId === 0
        ? 0
        : this.exam.basicInformation.positionId;
    // // 模版
    this.formData.templateId = +this.exam.templateId;
  }

  //离开前数据校验
  checkData() {
    let res = true;
    //表单
    const from = this.$refs["form"] as any;
    from?.validate((valid: any) => {
      res = valid;
    });
    if (!res) {
      this.$nextTick(() => {
        const isError = document.querySelector(".is-error") as HTMLElement;
        isError &&
          isError.scrollIntoView({
            block: "center",
            behavior: "smooth"
          });
      });
    }
    return res;
  }

  onDataChange() {
    this.exam.basicInformation.name = this.formData.name;
    this.exam.basicInformation.remark = this.formData.remark;
    this.exam.basicInformation.positionId = +this.formData.positionId;
    this.options.forEach(father => {
      father.positions.forEach(item => {
        if (item.positionId === +this.formData.positionId) {
          this.exam.basicInformation.positionName = item.name;
        }
      });
    });

    console.log(this.formData.positionId);
    console.log(this.exam.basicInformation.positionId);

    this.exam.templateId = this.formData.templateId;
    this.change();
  }

  @Emit()
  change() {
    return;
  }
}
